import type { FlightAirport } from 'api/flightsAPI';
import {
    onwardAPI,
    OnwardDestination,
    OnwardSuggestionSource,
    pickSuggestions,
} from 'api/onwardAPI';
import { quotesAPI } from 'api/quotesAPI';
import { useCallback, useEffect, useRef, useState } from 'react';

const AIRPORT_PROXIMITY_METERS = 1000

const haversineMeters = (
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number => {
  const R = 6371000
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.latitude - a.latitude)
  const dLng = toRad(b.longitude - a.longitude)
  const lat1 = toRad(a.latitude)
  const lat2 = toRad(b.latitude)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

export interface OnwardQuoteSuggestion {
  destination: OnwardDestination
  cheapestPrice: number
  currency: string
  vehicleTypeName: string
  /** Full vehicleDetails from the quote — contains icon, displayName,
   *  category, seats, luggage etc. (shape mirrors the quotes screen). */
  vehicleDetails?: any
  arrivalAirport: FlightAirport
  arrivalDateTime: string
  flightNumber: string
  source?: OnwardSuggestionSource
}

interface UseOnwardBookingsOptions {
  clientId: number | undefined
  leadPassengerId: number | undefined
  flightNumber: string | undefined
  pickupDateTime: string | undefined
  arrivalAirport: FlightAirport | undefined
  accountNumber: string | undefined
  numberOfPassengers: number | undefined
  passengers: any[] | undefined
  mainPassenger: any | undefined
  currentStep: string
}

interface UseOnwardBookingsResult {
  suggestions: OnwardQuoteSuggestion[]
  isLoadingLocations: boolean
  isLoadingQuotes: boolean
}

export default function useOnwardBookings({
  clientId,
  leadPassengerId,
  flightNumber,
  pickupDateTime,
  arrivalAirport,
  accountNumber,
  numberOfPassengers,
  passengers,
  mainPassenger,
  currentStep,
}: UseOnwardBookingsOptions): UseOnwardBookingsResult {
  const [destinations, setDestinations] = useState<OnwardDestination[]>([])
  const [suggestions, setSuggestions] = useState<OnwardQuoteSuggestion[]>([])
  const [isLoadingLocations, setIsLoadingLocations] = useState(false)
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false)

  const locationRequestRef = useRef('')
  const quotesRequestedRef = useRef(false)

  // Fetch onward locations when flight data is available
  useEffect(() => {
    const trimmedFlight = flightNumber?.trim() || ''
    console.log('[Onward] fetch gate', {
      trimmedFlight,
      pickupDateTime,
      clientId,
      leadPassengerId,
    })
    if (!trimmedFlight || !pickupDateTime || clientId == null) {
      console.log('[Onward] fetch gate bailed')
      setDestinations([])
      return
    }

    const requestKey = `${trimmedFlight}-${pickupDateTime}-${clientId}`
    if (requestKey === locationRequestRef.current) {
      console.log('[Onward] fetch gate deduped', requestKey)
      return
    }
    locationRequestRef.current = requestKey
    quotesRequestedRef.current = false // reset quotes for new locations
    console.log('[Onward] calling retrieveOnwardLocations', requestKey)

    setIsLoadingLocations(true)
    onwardAPI
      .retrieveOnwardLocations({
        clientId,
        leadPassengerId: leadPassengerId ?? undefined,
        flightNumber: trimmedFlight,
        pickupDateTime,
      })
      .then((response) => {
        console.log('[Onward] retrieveOnwardLocations response', response)
        const picked = pickSuggestions(response)
        console.log('[Onward] picked destinations', picked)
        setDestinations(picked)
      })
      .catch((err) => {
        console.log('[Onward] retrieveOnwardLocations error', err)
        setDestinations([])
      })
      .finally(() => {
        setIsLoadingLocations(false)
      })
  }, [flightNumber, pickupDateTime, clientId, leadPassengerId])

  // Fetch quotes when user reaches the vehicle step and we have destinations
  const fetchQuotes = useCallback(async () => {
    if (
      !destinations.length ||
      !arrivalAirport?.latitude ||
      !arrivalAirport?.longitude ||
      !accountNumber ||
      !pickupDateTime ||
      !flightNumber
    ) {
      return
    }

    setIsLoadingQuotes(true)

    // Build passengers array matching VehicleStep pattern
    const quotePassengers = [
      ...(passengers || [])
        .filter((p: any) => p && Object.keys(p).length > 0)
        .map((p: any) => ({
          contactNumber: p.contactNumber,
          email: p.email,
          isLeadPassenger: p.isLeadPassenger,
          name: p.name,
          userId: p.userId,
        })),
      ...(mainPassenger ? [mainPassenger] : []),
    ]

    const airportLat = arrivalAirport.latitude
    const airportLng = arrivalAirport.longitude
    const nearbyFiltered = destinations.filter((dest) => {
      if (dest.latitude == null || dest.longitude == null) return false
      if (airportLat == null || airportLng == null) return true
      const distance = haversineMeters(
        { latitude: airportLat, longitude: airportLng },
        { latitude: dest.latitude, longitude: dest.longitude },
      )
      return distance > AIRPORT_PROXIMITY_METERS
    })

    if (!nearbyFiltered.length) {
      setSuggestions([])
      setIsLoadingQuotes(false)
      return
    }

    const quotePromises = nearbyFiltered.map((dest) =>
      quotesAPI
        .createQuote({
          clientAccountNumber: accountNumber,
          numberOfPassengers: numberOfPassengers || 1,
          passengers: quotePassengers,
          journeyTypeName: 'OneWay',
          outbound: {
            pickup: {
              addressLine1: arrivalAirport.name,
              latitude: arrivalAirport.latitude,
              longitude: arrivalAirport.longitude,
              countryCode: arrivalAirport.countryCode,
            },
            dropOff: {
              addressLine1: dest.addressLine1,
              addressLine2: dest.addressLine2,
              town: dest.town,
              postCode: dest.postCode,
              country: dest.country,
              countryCode: dest.countryCode,
              latitude: dest.latitude,
              longitude: dest.longitude,
            },
            when: pickupDateTime,
            flightDetails: { flightNumber: flightNumber.trim() },
          },
        })
        .then((response) => {
          const vehicles = (response?.data?.outbound?.vehicles ?? []).filter(
            (v: any) => v?.isOffline !== true,
          )
          if (!vehicles.length) return null

          // Find cheapest vehicle by estimatedTotal.price
          const cheapest = vehicles.reduce(
            (min: any, v: any) => {
              const price = v?.estimatedTotal?.price ?? Infinity
              return price < min.price ? { price, vehicle: v } : min
            },
            { price: Infinity, vehicle: null },
          )

          if (!cheapest.vehicle) return null

          return {
            destination: dest,
            cheapestPrice: cheapest.price,
            currency: cheapest.vehicle.estimatedTotal?.currency ?? 'GBP',
            vehicleTypeName:
              cheapest.vehicle.vehicleDetails?.vehicleTypeName ?? '',
            vehicleDetails: cheapest.vehicle.vehicleDetails,
            arrivalAirport,
            arrivalDateTime: pickupDateTime,
            flightNumber: flightNumber.trim(),
            source: dest.source,
          } as OnwardQuoteSuggestion
        })
        .catch(() => null),
    )

    const results = await Promise.allSettled(quotePromises)
    const validSuggestions = results
      .filter(
        (r): r is PromiseFulfilledResult<OnwardQuoteSuggestion | null> =>
          r.status === 'fulfilled',
      )
      .map((r) => r.value)
      .filter((s): s is OnwardQuoteSuggestion => s !== null)

    setSuggestions(validSuggestions)
    setIsLoadingQuotes(false)
  }, [
    destinations,
    arrivalAirport,
    accountNumber,
    numberOfPassengers,
    passengers,
    mainPassenger,
    pickupDateTime,
    flightNumber,
  ])

  // Trigger quote fetch when entering vehicle step and all data is ready
  useEffect(() => {
    if (
      currentStep === 'vehicle' &&
      destinations.length > 0 &&
      arrivalAirport?.latitude &&
      accountNumber &&
      !quotesRequestedRef.current
    ) {
      quotesRequestedRef.current = true
      fetchQuotes()
    }
  }, [currentStep, destinations, arrivalAirport, accountNumber, fetchQuotes])

  return { suggestions, isLoadingLocations, isLoadingQuotes }
}
